import { BottomOrRegularModal } from '@popsure/dirty-swan';
import { Trans, useTranslation } from 'next-i18next';
import { useState } from 'react';
import AnimateHeight from 'react-animate-height';

import styles from './ShortTermQuestions.module.scss';

export const ShortTermQuestions = ({
  isDependent,
  hasEUPassport,
  hasPermanentResidencyPermit,
  isPlanningToStayMoreThan5Years,
  setHasEUPassport,
  setHasPermanentResidencyPermit,
  setIsPlanningToStayMoreThan5Years,
}: {
  isDependent?: boolean;
  hasEUPassport?: boolean;
  hasPermanentResidencyPermit?: boolean;
  isPlanningToStayMoreThan5Years?: boolean;
  setHasEUPassport: (value: boolean) => void;
  setHasPermanentResidencyPermit: (value: boolean) => void;
  setIsPlanningToStayMoreThan5Years: (value: boolean) => void;
}) => {
  const { t } = useTranslation();

  const [showEUModal, setShowEUModal] = useState(false);

  return (
    <>
      <BottomOrRegularModal
        isOpen={showEUModal}
        title={t('privatehealth:calculator.euModal.title', 'EU/EEA countries')}
        onClose={() => setShowEUModal(false)}
      >
        <p className="p-p p24 pt16 wmx6">
          {t(
            'privatehealth:calculator.euModal.description',
            'The EEA includes all EU countries, as well as Iceland, Liechtenstein and Norway.'
          )}
        </p>
      </BottomOrRegularModal>
      <legend className="p-p tc-grey-600 w100 mt24 mb8">
        {!isDependent
          ? t(
              'privatehealth:calculator.euPassport.questionStart',
              'Do you hold an'
            )
          : t(
              'privatehealth:calculator.euPassport.questionStart.dependent',
              'Do they hold an'
            )}{' '}
        <Trans i18nKey="privatehealth:calculator.euPassport.questionEnd">
          <button
            className="ds-interactive-component p-a fw-bold"
            type="button"
            onClick={() => setShowEUModal(true)}
          >
            EU/EEA
          </button>{' '}
          passport?
        </Trans>
      </legend>
      <div className="d-flex f-wrap gap8 w100">
        <div className={styles.half}>
          <input
            name="EU_PASSPORT"
            className="p-radio"
            type="radio"
            id="HAS_EU_PASSPORT"
            value="HAS_EU_PASSPORT"
            checked={!!hasEUPassport}
            onChange={() => {
              setHasEUPassport(true);
            }}
          />
          <label
            htmlFor="HAS_EU_PASSPORT"
            className="p-label p-label--bordered"
          >
            {t('privatehealth:calculator.yes', 'Yes')}
          </label>
        </div>
        <div className={styles.half}>
          <input
            name="EU_PASSPORT"
            className="p-radio"
            type="radio"
            id="HAS_NO_EU_PASSPORT"
            value="HAS_NO_EU_PASSPORT"
            checked={hasEUPassport !== undefined && !hasEUPassport}
            onChange={() => {
              setHasEUPassport(false);
            }}
          />
          <label
            htmlFor="HAS_NO_EU_PASSPORT"
            className="p-label p-label--bordered"
          >
            {t('privatehealth:calculator.no', 'No')}
          </label>
        </div>
      </div>
      <AnimateHeight
        duration={300}
        height={hasEUPassport !== undefined && !hasEUPassport ? 'auto' : 0}
        className="w100"
      >
        <>
          <legend className="p-p tc-grey-600 w100 mt24 mb8">
            {!isDependent
              ? t(
                  'privatehealth:calculator.permanentResidencyPermit.question',
                  'Do you have a permanent residency permit in Germany?'
                )
              : t(
                  'privatehealth:calculator.permanentResidencyPermit.question.dependent',
                  'Do they have a permanent residency permit in Germany?'
                )}
          </legend>
          <div className="d-flex f-wrap gap8 w100">
            <div className={styles.half}>
              <input
                name="PERMANENT_RESIDENCY_PERMIT"
                className="p-radio"
                type="radio"
                id="HAS_PERMANENT_RESIDENCY_PERMIT"
                value="HAS_PERMANENT_RESIDENCY_PERMIT"
                checked={!!hasPermanentResidencyPermit}
                onChange={() => {
                  setHasPermanentResidencyPermit(true);
                }}
              />
              <label
                htmlFor="HAS_PERMANENT_RESIDENCY_PERMIT"
                className="p-label p-label--bordered"
              >
                {t('privatehealth:calculator.yes', 'Yes')}
              </label>
            </div>
            <div className={styles.half}>
              <input
                name="PERMANENT_RESIDENCY_PERMIT"
                className="p-radio"
                type="radio"
                id="HAS_NO_PERMANENT_RESIDENCY_PERMIT"
                value="HAS_NO_PERMANENT_RESIDENCY_PERMIT"
                checked={
                  hasPermanentResidencyPermit !== undefined &&
                  !hasPermanentResidencyPermit
                }
                onChange={() => {
                  setHasPermanentResidencyPermit(false);
                }}
              />
              <label
                htmlFor="HAS_NO_PERMANENT_RESIDENCY_PERMIT"
                className="p-label p-label--bordered"
              >
                {t('privatehealth:calculator.no', 'No')}
              </label>
            </div>
          </div>
        </>
      </AnimateHeight>
      <AnimateHeight
        duration={300}
        height={
          hasEUPassport !== undefined &&
          !hasEUPassport &&
          hasPermanentResidencyPermit !== undefined &&
          !hasPermanentResidencyPermit
            ? 'auto'
            : 0
        }
        className="w100"
      >
        <>
          <legend className="p-p tc-grey-600 w100 mt24 mb8">
            {!isDependent
              ? t(
                  'privatehealth:calculator.relocatedMoreThan4YearsAgo.question',
                  'Did you relocate to Germany more than 4 years ago?'
                )
              : t(
                  'privatehealth:calculator.relocatedMoreThan4YearsAgo.question.dependent',
                  'Did they relocate to Germany more than 4 years ago?'
                )}
          </legend>
          <div className="d-flex f-wrap gap8 w100">
            <div className={styles.half}>
              <input
                name="RELOCATED_MORE_THAN_4_YEARS_AGO"
                className="p-radio"
                type="radio"
                id="RELOCATED_MORE_THAN_4_YEARS_AGO"
                value="RELOCATED_MORE_THAN_4_YEARS_AGO"
                checked={!!isPlanningToStayMoreThan5Years}
                onChange={() => {
                  setIsPlanningToStayMoreThan5Years(true);
                }}
              />
              <label
                htmlFor="RELOCATED_MORE_THAN_4_YEARS_AGO"
                className="p-label p-label--bordered"
              >
                {t('privatehealth:calculator.yes', 'Yes')}
              </label>
            </div>
            <div className={styles.half}>
              <input
                name="RELOCATED_MORE_THAN_4_YEARS_AGO"
                className="p-radio"
                type="radio"
                id="DOESNT_PLAN_TO_STAY_LONGER_THAN_5_YEARS"
                value="DOESNT_PLAN_TO_STAY_LONGER_THAN_5_YEARS"
                checked={
                  isPlanningToStayMoreThan5Years !== undefined &&
                  !isPlanningToStayMoreThan5Years
                }
                onChange={() => {
                  setIsPlanningToStayMoreThan5Years(false);
                }}
              />
              <label
                htmlFor="DOESNT_PLAN_TO_STAY_LONGER_THAN_5_YEARS"
                className="p-label p-label--bordered"
              >
                {t('privatehealth:calculator.no', 'No')}
              </label>
            </div>
          </div>
        </>
      </AnimateHeight>
    </>
  );
};
